import { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function ForceChangePassword() {
  const { user, setUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('La nuova password deve avere almeno 8 caratteri'); return; }
    if (newPassword !== confirmPassword) { toast.error('Le password non coincidono'); return; }
    if (newPassword === currentPassword) { toast.error('La nuova password deve essere diversa da quella attuale'); return; }

    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      toast.success('Password aggiornata!');
      setUser(prev => ({ ...prev, must_change_password: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="force-change-password">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Cambio Password Obbligatorio</h1>
          <p className="text-sm text-slate-500 mt-2">Per la sicurezza del tuo account, devi impostare una nuova password.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password attuale</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Inserisci la password ricevuta" required
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm pr-10 focus:ring-2 focus:ring-sky-500" data-testid="current-password" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nuova password</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimo 8 caratteri" required minLength={8}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm pr-10 focus:ring-2 focus:ring-sky-500" data-testid="new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Conferma nuova password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Ripeti la nuova password" required
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" data-testid="confirm-password" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600" data-testid="change-password-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            Imposta Nuova Password
          </Button>
        </form>

        <div className="mt-4 text-center">
          <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-sky-500 transition-colors">
            Made with <span className="text-red-400">&hearts;</span> by <span className="font-semibold text-slate-500">TelcaVoIP.AI</span>
          </a>
        </div>
      </div>
    </div>
  );
}
